import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { RELEASE_STAMP_LONG } from "@/version";

export default function ClientGuidePage() {
  const navigate = useNavigate();

  const handlePrint = () => window.print();

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      {/* Screen-only toolbar */}
      <div className="print:hidden sticky top-0 z-50 bg-white border-b border-[#E5E5E5] px-6 py-3 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-[#525252] hover:text-[#0055FF]">
          <ArrowLeft size={16} /> Back
        </button>
        <Button onClick={handlePrint} className="bg-[#0055FF] hover:bg-[#0040CC] text-white font-bold rounded-none h-9 px-4 text-xs">
          <Printer size={14} className="mr-2" /> Print / Save as PDF
        </Button>
      </div>

      {/* Document */}
      <div className="max-w-[210mm] mx-auto bg-white shadow-sm print:shadow-none print:max-w-none">

        {/* Cover Page */}
        <div className="h-[297mm] print:h-auto flex flex-col items-center justify-center px-16 py-20 border-b border-[#E5E5E5] print:break-after-page">
          <img src="/eyvo-logo.png" alt="Eyvo" className="w-48 mb-10" />
          <h1 className="text-4xl font-black tracking-tight text-[#1a2b6d] text-center mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Client User Guide
          </h1>
          <p className="text-lg text-[#525252] text-center mb-2">Eyvo Project Delivery Platform</p>
          <div className="w-20 h-1 bg-gradient-to-r from-[#0055FF] to-[#7C3AED] my-8" />
          <p className="text-sm text-[#525252]">Your guide to tracking projects and collaborating with Eyvo</p>
          <div className="mt-auto text-center">
            <p className="text-xs text-[#A3A3A3]">Eyvo Inc. &middot; California, USA</p>
            <p className="text-xs text-[#A3A3A3] mt-1">Document Version 1.14 &middot; {RELEASE_STAMP_LONG}</p>
          </div>
        </div>

        {/* Table of Contents */}
        <div className="px-16 py-16 border-b border-[#E5E5E5] print:break-after-page">
          <h2 className="text-2xl font-black tracking-tight text-[#0A0A0A] mb-8" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Table of Contents</h2>
          <div className="space-y-3">
            {[
              { num: "1", title: "Getting Started", sub: ["1.1 Creating Your Account", "1.2 Setting Up Two-Factor Authentication", "1.3 Recovery Backup Codes", "1.4 Signing In"] },
              { num: "2", title: "Dashboard", sub: ["2.1 Viewing Your Projects", "2.2 Project Status Indicators"] },
              { num: "3", title: "Project Overview", sub: ["3.1 Project Header & Timeline", "3.2 Task Status Summary", "3.3 Team Members"] },
              { num: "4", title: "Tasks", sub: ["4.1 Kanban Board", "4.2 List View", "4.3 Gantt Chart", "4.4 Viewing Task Details"] },
              { num: "5", title: "Change Requests", sub: ["5.1 Submitting a Change Request", "5.2 Request Types"] },
              { num: "6", title: "Communication", sub: ["6.1 Project Chat", "6.2 Comments on Tasks"] },
              { num: "7", title: "Documents", sub: ["7.1 Viewing & Downloading Files", "7.2 Uploading Documents"] },
              { num: "8", title: "Your Profile", sub: ["8.1 Notification Preferences", "8.2 SMS & Email Alerts"] },
              { num: "9", title: "Security", sub: ["9.1 Password Requirements", "9.2 Forgot Password", "9.3 Two-Factor Authentication"] },
              { num: "10", title: "Support & Contact", sub: ["10.1 Getting Help"] },
            ].map(section => (
              <div key={section.num}>
                <div className="flex items-baseline gap-3">
                  <span className="text-sm font-bold text-[#0055FF] w-8">{section.num}</span>
                  <span className="text-sm font-bold text-[#0A0A0A]">{section.title}</span>
                </div>
                {section.sub.map(s => (
                  <div key={s} className="flex items-baseline gap-3 ml-4">
                    <span className="text-xs text-[#A3A3A3] w-8">{s.split(" ")[0]}</span>
                    <span className="text-xs text-[#525252]">{s.split(" ").slice(1).join(" ")}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Sections */}
        <div className="px-16 py-12 space-y-16 text-sm leading-relaxed text-[#333]">

          <Section num="1" title="Getting Started" anchor="getting-started">
            <Sub num="1.1" title="Creating Your Account">
              <p>To create your account, visit the Eyvo Project Delivery Platform and click the <strong>"Client"</strong> tab on the sign-in page, then click <strong>"Create an account"</strong>.</p>
              <p className="mt-2">You will need to provide:</p>
              <ul className="list-disc ml-6 mt-2 space-y-1">
                <li><strong>Full Name</strong> &mdash; Your name as it will appear in the system</li>
                <li><strong>Email Address</strong> &mdash; Must match your organization's approved domain (e.g., @yourcompany.com)</li>
                <li><strong>Mobile Phone Number</strong> &mdash; Optional. Only needed if you tick the SMS notifications consent box (see below)</li>
                <li><strong>Password</strong> &mdash; Must meet all security requirements (see Section 9)</li>
                <li><strong>SMS Notifications (optional)</strong> &mdash; Tick the consent checkbox if you'd like to receive project status updates by text. You can opt out at any time by replying STOP to any message.</li>
              </ul>
              <div className="bg-[#FEF3C7] border border-[#F59E0B]/20 p-3 mt-4 text-xs text-[#92400E]">
                <strong>If you see "Your email domain is not recognised":</strong> the system couldn't find your organisation in our allow-list. This happens when (a) your organisation hasn't been set up yet, or (b) you're using a personal email like Gmail/Hotmail instead of your work email. Either way, contact your <strong>Eyvo Project Manager</strong> &mdash; they can add your domain in 30 seconds and you can retry the signup.
              </div>
              <p className="mt-3 text-xs text-[#525252]">After clicking <strong>Create</strong>, you have <strong>60 minutes</strong> to complete Two-Factor Authentication setup (Section 1.2). If you take longer than that, just sign in with the email + password you just set and you'll be sent back through the 2FA setup.</p>
            </Sub>
            <Sub num="1.2" title="Setting Up Two-Factor Authentication">
              <p>After creating your account, you will be guided through <strong>Two-Factor Authentication (2FA)</strong> setup. This is mandatory for all client accounts.</p>
              <ol className="list-decimal ml-6 mt-2 space-y-2">
                <li>Download an authenticator app (Google Authenticator, Microsoft Authenticator, or Authy) on your mobile device</li>
                <li>Scan the QR code displayed on screen with your authenticator app</li>
                <li>Enter the 6-digit code shown in your authenticator app to verify</li>
              </ol>
              <div className="bg-[#FEF3C7] border border-[#F59E0B]/20 p-3 mt-3 text-xs text-[#92400E]">
                <strong>Important:</strong> If you cannot scan the QR code, you can manually enter the secret key shown below it into your authenticator app.
              </div>
            </Sub>
            <Sub num="1.3" title="Recovery Backup Codes">
              <p>During 2FA setup, you will receive <strong>8 one-time recovery codes</strong>. These codes can be used to sign in if you lose access to your authenticator app.</p>
              <div className="bg-[#FEF2F2] border border-[#DC2626]/20 p-3 mt-2 text-xs text-[#DC2626]">
                <strong>Save these codes securely.</strong> Each code can only be used once. Store them in a safe place separate from your device.
              </div>
            </Sub>
            <Sub num="1.4" title="Signing In">
              <p>To sign in, select the <strong>"Client"</strong> tab on the login page, enter your email and password, then provide the 6-digit code from your authenticator app when prompted.</p>
            </Sub>
          </Section>

          <Section num="2" title="Dashboard">
            <Sub num="2.1" title="Viewing Your Projects">
              <p>After signing in, you will see the Dashboard with all projects you have been assigned to. Each project is displayed as a card with key information including:</p>
              <ul className="list-disc ml-6 mt-2 space-y-1">
                <li>Project name and your organization name</li>
                <li>Current status indicator (On Track, At Risk, or Off Track)</li>
                <li>Task completion progress bar (blue)</li>
                <li>Overall project timeline bar (green-to-purple gradient)</li>
              </ul>
              <p className="mt-2">Click on any project card to open the full project view.</p>
            </Sub>
            <Sub num="2.2" title="Project Status Indicators">
              <p>Each project has a RAG (Red, Amber, Green) status:</p>
              <ul className="mt-2 space-y-2">
                <li className="flex items-center gap-2"><span className="w-3 h-3 bg-[#16A34A] flex-shrink-0" /> <strong>On Track (Green)</strong> &mdash; Project is progressing as planned</li>
                <li className="flex items-center gap-2"><span className="w-3 h-3 bg-[#D97706] flex-shrink-0" /> <strong>At Risk (Amber)</strong> &mdash; Potential issues that may affect delivery</li>
                <li className="flex items-center gap-2"><span className="w-3 h-3 bg-[#DC2626] flex-shrink-0" /> <strong>Off Track (Red)</strong> &mdash; Significant issues requiring attention</li>
              </ul>
            </Sub>
          </Section>

          <Section num="3" title="Project Overview" pageBreak>
            <Sub num="3.1" title="Project Header & Timeline">
              <p>At the top of each project page, you will see the project name, client organization, Eyvo Project Manager details, and the master timeline showing the project schedule from start to completion date.</p>
            </Sub>
            <Sub num="3.2" title="Task Status Summary">
              <p>The Overview tab displays task counts grouped by status: To Do, In Progress, Review, and Done. This gives you a quick snapshot of project progress.</p>
            </Sub>
            <Sub num="3.3" title="Team Members">
              <p>The team section shows all members assigned to the project, including both Eyvo internal staff and client team members, along with their roles.</p>
            </Sub>
          </Section>

          <Section num="4" title="Tasks">
            <Sub num="4.1" title="Kanban Board">
              <p>The <strong>Kanban</strong> tab displays tasks organized in columns by status. This provides a visual overview of what's being worked on and what's completed.</p>
            </Sub>
            <Sub num="4.2" title="List View">
              <p>The <strong>List</strong> tab shows all tasks in a detailed table format. You can see task titles, statuses, assignees, dates, duration, and priority at a glance.</p>
            </Sub>
            <Sub num="4.3" title="Gantt Chart">
              <p>The <strong>Gantt</strong> tab provides a timeline visualization of all tasks, showing dependencies and scheduling.</p>
            </Sub>
            <Sub num="4.4" title="Viewing Task Details">
              <p>Click on any task to view its full details including description, comments, dates, and assignee information. You can add public comments visible to both your team and the Eyvo team.</p>
            </Sub>
          </Section>

          <Section num="5" title="Change Requests" pageBreak>
            <Sub num="5.1" title="Submitting a Change Request">
              <p>Navigate to the <strong>Change Requests</strong> tab within your project. Click <strong>"+ New Request"</strong> and provide:</p>
              <ul className="list-disc ml-6 mt-2 space-y-1">
                <li><strong>Title</strong> &mdash; A brief summary of what you need</li>
                <li><strong>Description</strong> &mdash; Detailed explanation of the requested change</li>
                <li><strong>Request Type</strong> &mdash; Select the most appropriate category</li>
                <li><strong>Cost Impact</strong> &mdash; Your estimate of the expected cost (optional)</li>
                <li><strong>Timeline Impact</strong> &mdash; Any expected schedule impact</li>
              </ul>
            </Sub>
            <Sub num="5.2" title="Request Types">
              <ul className="mt-2 space-y-2">
                <li className="flex items-center gap-2"><span className="w-2 h-2 bg-[#DC2626] flex-shrink-0" /> <strong>Bug Fix</strong> &mdash; Something is not working as expected</li>
                <li className="flex items-center gap-2"><span className="w-2 h-2 bg-[#2563EB] flex-shrink-0" /> <strong>New Feature</strong> &mdash; A new capability or function</li>
                <li className="flex items-center gap-2"><span className="w-2 h-2 bg-[#7C3AED] flex-shrink-0" /> <strong>Enhancement</strong> &mdash; Improvement to existing functionality</li>
                <li className="flex items-center gap-2"><span className="w-2 h-2 bg-[#D97706] flex-shrink-0" /> <strong>Configuration Change</strong> &mdash; System settings adjustment</li>
                <li className="flex items-center gap-2"><span className="w-2 h-2 bg-[#0891B2] flex-shrink-0" /> <strong>Data Request</strong> &mdash; Data export, import, or modification</li>
              </ul>
            </Sub>
          </Section>

          <Section num="6" title="Communication">
            <Sub num="6.1" title="Project Chat">
              <p>The <strong>Chat</strong> tab provides real-time messaging with the Eyvo project team. Messages are delivered instantly and stored for reference.</p>
            </Sub>
            <Sub num="6.2" title="Comments on Tasks">
              <p>You can add comments on tasks that are visible to both your team and the Eyvo team. This is ideal for asking questions or providing feedback on specific deliverables.</p>
              <div className="bg-[#F0F7FF] border border-[#0055FF]/20 p-3 mt-2 text-xs text-[#0055FF]">
                <strong>Note:</strong> Some comments may be marked as "Internal" by the Eyvo team. These are not visible to client users and are used for internal coordination.
              </div>
            </Sub>
          </Section>

          <Section num="7" title="Documents">
            <Sub num="7.1" title="Viewing & Downloading Files">
              <p>The <strong>Documents</strong> tab shows all files shared for the project. Click on any file to download it.</p>
            </Sub>
            <Sub num="7.2" title="Uploading Documents">
              <p>You can upload documents to share with the Eyvo team. Supported formats include PDFs, images, spreadsheets, and common document types.</p>
            </Sub>
          </Section>

          <Section num="8" title="Your Profile" pageBreak>
            <Sub num="8.1" title="Notification Preferences">
              <p>Click <strong>"My Profile"</strong> in the sidebar to access your settings. Here you can manage which notifications you receive.</p>
            </Sub>
            <Sub num="8.2" title="SMS & Email Alerts">
              <p>You can independently toggle:</p>
              <ul className="list-disc ml-6 mt-2 space-y-1">
                <li><strong>Email Alerts</strong> &mdash; Receive email notifications for project updates, CR status changes, and estimates</li>
                <li><strong>SMS Alerts</strong> &mdash; Receive text message notifications (requires a phone number on file)</li>
              </ul>
              <p className="mt-2">You can opt out of SMS notifications at any time by replying STOP to any message or by disabling the toggle in your profile.</p>
            </Sub>
          </Section>

          <Section num="9" title="Security">
            <Sub num="9.1" title="Password Requirements">
              <p>Your password must meet all of the following requirements:</p>
              <ul className="list-disc ml-6 mt-2 space-y-1">
                <li>At least 10 characters long</li>
                <li>At least one uppercase letter</li>
                <li>At least one lowercase letter</li>
                <li>At least one number</li>
                <li>At least one special character</li>
              </ul>
            </Sub>
            <Sub num="9.2" title="Forgot Password">
              <p>If you forget your password, click <strong>"Forgot your password?"</strong> on the Client login tab. Enter your email address and you will receive a reset code. Enter the code along with your new password to regain access.</p>
            </Sub>
            <Sub num="9.3" title="Two-Factor Authentication">
              <p>2FA is required for all sign-ins. If you lose access to your authenticator app, use one of your recovery backup codes to sign in. Contact your Eyvo Project Manager if you have exhausted all recovery codes.</p>
            </Sub>
          </Section>

          <Section num="10" title="Support & Contact">
            <Sub num="10.1" title="Getting Help">
              <p>For any questions or issues with the platform, please contact your assigned Eyvo Project Manager directly via the project Chat, or email the Eyvo support team.</p>
              <p className="mt-2">Your Project Manager's name and email are displayed at the top of each project page for easy reference.</p>
            </Sub>
          </Section>

          <Section num="11" title="FAQ — How do I…" pageBreak>
            <Sub num="11.1" title="How do I find a specific project?">
              <p>Click <strong>Projects</strong> in the left sidebar. The list shows every project your organisation has with Eyvo. Click any row to open the project detail page. Use the search box at the top of the list to filter by project name.</p>
            </Sub>
            <Sub num="11.2" title="How do I see the latest updates on my project?">
              <p>Open the project → <strong>Activity</strong> tab. Every comment, file upload, status change, and assignment appears in chronological order, newest first.</p>
            </Sub>
            <Sub num="11.3" title="How do I chat with the Eyvo team?">
              <p>Open the project → <strong>Chat</strong> tab. Type your message and press Enter. To attach a file, click the paperclip icon. Replies appear in real time — both your team and the Eyvo team see new messages instantly.</p>
            </Sub>
            <Sub num="11.4" title="How do I create a new task?">
              <p>Open the project → <strong>Tasks</strong> tab → click <strong>+ New Task</strong>. Enter a clear title and description. Eyvo will assign it to the right person and update you when it changes status.</p>
            </Sub>
            <Sub num="11.5" title="How do I see my invoices and contracts?">
              <p>Click your organisation name in the sidebar to open your <strong>Client Overview</strong>. The <em>Billing</em> tab lists every invoice and contract document. Click any row to view or download the PDF.</p>
            </Sub>
            <Sub num="11.6" title="How do I approve a proposal?">
              <p>When Eyvo sends a proposal, you receive an email with a one-click <strong>"View Proposal"</strong> button. The proposal page has an <em>Accept</em> button at the bottom. Clicking Accept signs the proposal electronically and notifies Eyvo immediately.</p>
            </Sub>
            <Sub num="11.7" title="How do I add a colleague to the platform?">
              <p>Contact your Eyvo Project Manager with the colleague's name, email, and the access level they need. Eyvo will create the account and the colleague will receive a welcome email with sign-in instructions.</p>
            </Sub>
            <Sub num="11.8" title="What if I can't sign in?">
              <p>Your account is restricted to your work email domain — personal Gmail/Yahoo addresses will not work. If your work email is rejected, contact your Eyvo Project Manager — they can add your domain to the allow-list in 30 seconds.</p>
            </Sub>
          </Section>
        </div>
        <div className="px-16 py-12 border-t border-[#E5E5E5] text-center">
          <img src="/eyvo-logo.png" alt="Eyvo" className="w-20 mx-auto mb-3 opacity-40" />
          <p className="text-xs text-[#A3A3A3]">&copy; 2026 Eyvo Inc. All rights reserved.</p>
          <p className="text-xs text-[#A3A3A3] mt-1">Eyvo Project Delivery Platform &mdash; California, USA</p>
        </div>
      </div>
    </div>
  );
}

function Section({ num, title, children, pageBreak, anchor }) {
  return (
    <div id={anchor || undefined} className={pageBreak ? "print:break-before-page scroll-mt-8" : "scroll-mt-8"}>
      <div className="flex items-center gap-3 mb-6 pb-3 border-b-2 border-[#0055FF]">
        <span className="text-3xl font-black text-[#0055FF]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{num}</span>
        <h2 className="text-2xl font-black tracking-tight text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{title}</h2>
      </div>
      <div className="space-y-8">{children}</div>
    </div>
  );
}

function Sub({ num, title, children }) {
  return (
    <div>
      <h3 className="text-base font-bold text-[#1a2b6d] mb-2">
        <span className="text-[#0055FF] mr-2">{num}</span>{title}
      </h3>
      <div className="text-sm text-[#333] leading-relaxed">{children}</div>
    </div>
  );
}
